import {di} from "@dp-instant/core";
import {TypesCore, IEventEmitter} from "@dp-instant/interfaces";
import {log} from "@dp-instant/utils";
import {SIOConstants} from "@game/constants";
import {GameEvents} from "@game/coregame/GameEvents";
import {CookieModelBase} from "@game/coregame/model/CookieModelBase";
import {
    GameColors,
    ISelectableBuilding,
    ISelectableColor,
    ISelectableFighter,
    PlayerColor,
    SelectableBuildingDataSet,
    SelectableColorsDataSet,
    SelectableFighterDataSet,
    UserSelectableColorsSet
} from "@game/coregame/settings";
import {injectable} from "inversify";
import {CTXHistory} from "../interfaces/CTXHistory";
import {CTXLevelProgress} from "../interfaces/CTXLevelProgress";
import {PlayerType} from "../interfaces/PlayerType";

type Value = number | string | boolean;
type StoreType = {[key: string]: Value};

export enum CookieModelKey {
    coins = "coins",
    bot_level = "bot_level",
    fights_passed = "fights_passed",
    wins = "wins",
    loses = "loses",
    absoluteTryNum = "absoluteTryNum",
    absoluteLevelNum = "absoluteLevelNum",
    localLevelNum = "localLevelNum",
    currentStage = "currentStage",
    playerOfflineLevel = "playerOfflineLevel",
    playerSpawnLevel = "playerSpawnLevel",
    playerStartPopulation = "playerStartPopulation",
    lastLaunch = "lastLaunch",
    timeDiff = "timeDiff",
    ctx_history = "ctx_history",
    vip = "vip",
    //
    selected_color_set_id = "selected_color_set_id",
    selected_building_id = "selected_building_id",
    selected_fighter_id = "selected_fighter_id",
    user_buildings_set = "user_buildings_set",
    user_fighter_set = "user_fighter_set"
}

@injectable()
export class CookieModel extends CookieModelBase {
    protected getDefaultStore() {
        return {
            [CookieModelKey.coins]: 0,
            [CookieModelKey.bot_level]: 0,
            [CookieModelKey.fights_passed]: 0,
            [CookieModelKey.wins]: 0,
            [CookieModelKey.loses]: 0,
            [CookieModelKey.absoluteTryNum]: 0,
            [CookieModelKey.absoluteLevelNum]: 1,
            [CookieModelKey.localLevelNum]: 1,
            [CookieModelKey.currentStage]: 0,
            [CookieModelKey.playerOfflineLevel]: 1,
            [CookieModelKey.playerSpawnLevel]: 1,
            [CookieModelKey.playerStartPopulation]: 1,
            [CookieModelKey.lastLaunch]: 0,
            [CookieModelKey.timeDiff]: 0,
            [CookieModelKey.ctx_history]: "{}",
            //
            [CookieModelKey.selected_color_set_id]: UserSelectableColorsSet.BLUE,
            [CookieModelKey.selected_building_id]: 1,
            [CookieModelKey.selected_fighter_id]: 1,
            [CookieModelKey.user_buildings_set]: "[1]",
            [CookieModelKey.user_fighter_set]: "[1]",
            [CookieModelKey.vip]: false
        };
    }
    public get playerOfflineLevel(): number {
        return <number>this.get(CookieModelKey.playerOfflineLevel);
    }
    public set playerOfflineLevel(value: number) {
        this.set(CookieModelKey.playerOfflineLevel, value);
    }
    public get playerSpawnLevel(): number {
        return <number>this.get(CookieModelKey.playerSpawnLevel);
    }
    public set playerSpawnLevel(value: number) {
        this.set(CookieModelKey.playerSpawnLevel, value);
    }
    public set absoluteLevelNum(value: number) {
        this.set(CookieModelKey.absoluteLevelNum, value);
    }
    public get absoluteLevelNum(): number {
        return <number>this.get(CookieModelKey.absoluteLevelNum);
    }
    public set absoluteTryNum(value: number) {
        this.set(CookieModelKey.absoluteTryNum, value);
    }
    public get absoluteTryNum(): number {
        return <number>this.get(CookieModelKey.absoluteTryNum);
    }
    public get coins(): number {
        return <number>this.get(CookieModelKey.coins) * 1;
    }
    public set coins(value: number) {
        this.set(CookieModelKey.coins, value);
        const dispatcher: IEventEmitter = di.get(TypesCore.dispatcher);
        dispatcher.emit(GameEvents.COINS_UPDATED, {coins: this.coins});
    }

    public get playerStartPopulation(): number {
        return <number>this.get(CookieModelKey.playerStartPopulation);
    }
    public set playerStartPopulation(value: number) {
        this.set(CookieModelKey.playerStartPopulation, value);
    }
    public get currentStage(): number {
        return <number>this.get(CookieModelKey.currentStage);
    }
    public set currentStage(value: number) {
        this.set(CookieModelKey.currentStage, value);
    }
    public isEnoughCoins(cost: number): boolean {
        return this.coins - cost >= 0;
    }
    public get fightsPassed(): number {
        return <number>this.get(CookieModelKey.fights_passed);
    }

    public set fightsPassed(val: number) {
        this.set(CookieModelKey.fights_passed, val);
    }

    public set setVip(val: boolean) {
        this.set(CookieModelKey.vip, val);
    }

    public get getVip() {
        return <boolean>this.get(CookieModelKey.vip);
    }

    public get botLevel(): number {
        return <number>this.get(CookieModelKey.bot_level);
    }

    public set botLevel(val: number) {
        this.set(CookieModelKey.bot_level, val);
    }

    public spendCoins(amount: number) {
        this.coins -= amount;
    }

    public get lastLaunch(): number {
        return <number>this.get(CookieModelKey.lastLaunch);
    }
    public set lastLaunch(value: number) {
        this.set(CookieModelKey.lastLaunch, value);
    }

    public get timeDiff(): number {
        return <number>this.get(CookieModelKey.timeDiff);
    }

    public set timeDiff(value: number) {
        this.set(CookieModelKey.timeDiff, value);
    }

    public get wins(): number {
        return <number>this.get(CookieModelKey.wins);
    }

    public set wins(value: number) {
        this.set(CookieModelKey.wins, value);
    }

    public get loses(): number {
        return <number>this.get(CookieModelKey.loses);
    }

    public set loses(value: number) {
        this.set(CookieModelKey.loses, value);
    }

    //

    async sync(): Promise<void> {
        await super.sync();
        this.syncTime();
    }

    /***
     * @todo - It's need to discuss how to paused this correctly when player in the context.
     * @see - StartUpAction.tryToShowOfflineEarningsPopup
     */
    public syncTime(): void {
        const paused: boolean = !this.social.inSolo;

        const now = Date.now();
        if (this.lastLaunch === 0) {
            this.lastLaunch = now;
        }
        if (!paused) {
            this.timeDiff = now - this.lastLaunch;
        }

        if (this.timeDiff >= SIOConstants.MAX_REWARD_CAP_IN_MS) {
            this.timeDiff = SIOConstants.MAX_REWARD_CAP_IN_MS;
        }
        if (!paused) {
            this.lastLaunch = now;
        }
    }

    public getContextHistory(ctxId: string): CTXLevelProgress | undefined {
        const historyString = <string>this.get(CookieModelKey.ctx_history);
        let historyObject: CTXHistory = {};
        try {
            historyObject = JSON.parse(historyString);
        } catch (e) {
            log.warn("Failed to load ctx history", historyString);
        }
        const len = historyObject[ctxId].length;
        return historyObject[ctxId][len - 1];
    }
    public getContextHistories(): CTXHistory {
        // TODO: temporary method, just for debug purposes
        const historyString = <string>this.get(CookieModelKey.ctx_history);
        let historyObject: CTXHistory = {};
        try {
            historyObject = JSON.parse(historyString);
        } catch (e) {
            log.warn("Failed to load ctx history", historyString);
        }

        return historyObject;
    }
    public setContextHistory(ctxId: string, data: CTXLevelProgress): void {
        const historyString = <string>this.get(CookieModelKey.ctx_history);
        let historyObject: CTXHistory = {};
        try {
            historyObject = JSON.parse(historyString);
        } catch (e) {
            log.warn("Failed to load ctx history");
        }
        if (historyObject) {
            const history = historyObject[ctxId];
            const isEnded = data.c >= data.s.length - 1;
            if (isEnded) {
                if (history.length < 3) {
                    history.push(data);
                } else {
                    history.shift();
                    history.push(data);
                }
            } else {
                //
            }
        }
        this.set(CookieModelKey.ctx_history, JSON.stringify(historyObject));
    }

    public get selectedColorSet(): ISelectableColor | undefined {
        let colorSetId: UserSelectableColorsSet = <UserSelectableColorsSet>(
            this.get(CookieModelKey.selected_color_set_id)
        );
        return SelectableColorsDataSet.filter(item => item.id === colorSetId).map(item => ({
            ...item,
            selected: true,
            stored: true
        }))[0];
    }

    public get selectedBuilding(): ISelectableBuilding | undefined {
        return SelectableBuildingDataSet.filter(
            item => item.id === this.get(CookieModelKey.selected_building_id)
        ).map(item => ({
            ...item,
            selected: true,
            stored: true
        }))[0];
    }

    public get selectedFighter(): ISelectableFighter | undefined {
        return SelectableFighterDataSet.filter(
            item => item.id === this.get(CookieModelKey.selected_fighter_id)
        ).map(item => ({
            ...item,
            selected: true,
            stored: true
        }))[0];
    }

    ///
    public get user_buildings_set(): string {
        return <string>this.get(CookieModelKey.user_buildings_set);
    }

    public getUserBuildingSet(): number[] {
        let ids: number[] = [];
        try {
            //console.dir(this.user_buildings_set);
            //ids = ids.concat(JSON.parse(this.user_buildings_set));
            ids = ids.concat(
                Array.isArray(this.user_buildings_set)
                    ? this.user_buildings_set
                    : JSON.parse(this.user_buildings_set)
            );
        } catch (e) {
            log.warn("Failed to load user stored buildings");
        }
        return ids;
    }
    public getUserFighterSet(): number[] {
        let uids: number[] = [];
        try {
            uids = uids.concat(
                Array.isArray(this.user_fighter_set)
                    ? this.user_fighter_set
                    : JSON.parse(this.user_fighter_set)
            );
        } catch (e) {
            log.warn("Failed to load user stored fighters");
        }
        return uids;
    }
    public get user_fighter_set(): string {
        return <string>this.get(CookieModelKey.user_fighter_set);
    }

    // public get availableBuildings(): ISelectableBuilding[] {
    //     let result: ISelectableBuilding[] = [];
    //     const storedBuildingsString = <string>this.get(CookieModelKey.user_buildings_set);
    //     let userStoredBuildingsId: number[] = [];
    //     try {
    //         userStoredBuildingsId = JSON.parse(storedBuildingsString);
    //     } catch (e) {
    //         log.warn("Failed to load user stored buildings");
    //     }

    //     result = SelectableBuildingDataSet.filter(
    //         item => this.absoluteLevelNum >= item.minPlayerLevel
    //     ).map(item => {
    //         const selected: boolean = item.id === this.get(CookieModelKey.selected_building_id);
    //         const stored: boolean = userStoredBuildingsId.indexOf(item.id) !== -1;
    //         return {...item, selected, stored};
    //     });
    //     return result;
    // }

    // public get availableFighters(): ISelectableFighter[] {
    //     let result: ISelectableFighter[] = [];
    //     const storedFightersString = <string>this.get(CookieModelKey.user_fighter_set);
    //     let userStoredFightersId: number[] = [];
    //     try {
    //         userStoredFightersId = JSON.parse(storedFightersString);
    //     } catch (e) {
    //         log.warn("Failed to load user stored fighters");
    //     }

    //     result = SelectableFighterDataSet.filter(
    //         item => this.absoluteLevelNum >= item.minPlayerLevel
    //     ).map(item => {
    //         const selected: boolean = item.id === this.get(CookieModelKey.selected_fighter_id);
    //         const stored: boolean = userStoredFightersId.indexOf(item.id) !== -1;
    //         return {...item, selected, stored};
    //     });

    //     return result;
    // }

    public get availableColors(): ISelectableColor[] {
        return SelectableColorsDataSet.map(item => {
            const selected: boolean =
                item.id === <UserSelectableColorsSet>this.get(CookieModelKey.selected_color_set_id);
            return {...item, stored: true, selected};
        });
    }

    public addUserBuilding(id: number) {
        this.addUserSet(id, CookieModelKey.user_buildings_set);
    }
    public addUserFighter(id: number) {
        this.addUserSet(id, CookieModelKey.user_fighter_set);
    }

    protected addUserSet(id: number, key: string) {
        const setString = <string>this.get(key);
        try {
            // console.dir(setString);
            // const ids: number[] = [].concat(JSON.parse(setString));
            const ids: number[] = [].concat(
                Array.isArray(setString) ? setString : JSON.parse(setString)
            );

            if (ids.indexOf(id) === -1) {
                ids.push(id);
            }

            this.set(key, JSON.stringify(ids));
        } catch (e) {
            log.warn("Failed to load user stored fighters");
        }
    }

    public get selected_color_set_id(): UserSelectableColorsSet {
        return <UserSelectableColorsSet>this.get(CookieModelKey.selected_color_set_id);
    }
    public set selected_color_set_id(colorSet: UserSelectableColorsSet) {
        if (colorSet !== <UserSelectableColorsSet>this.get(CookieModelKey.selected_color_set_id)) {
            this.set(CookieModelKey.selected_color_set_id, colorSet);
            this.onUserSelectableKeyChanged();
        }
    }

    public set selected_building_id(id: number) {
        if (id !== <number>this.get(CookieModelKey.selected_building_id)) {
            this.set(CookieModelKey.selected_building_id, id);
            this.onUserSelectableKeyChanged();
        }
    }

    public get selected_fighter_id(): number {
        return <number>this.get(CookieModelKey.selected_fighter_id) * 1;
    }
    public get selected_building_id(): number {
        return <number>this.get(CookieModelKey.selected_building_id) * 1;
    }
    public set selected_fighter_id(id: number) {
        if (id !== <number>this.get(CookieModelKey.selected_fighter_id)) {
            this.set(CookieModelKey.selected_fighter_id, id);
            this.onUserSelectableKeyChanged();
        }
    }

    private onUserSelectableKeyChanged(): void {
        const dispatcher: IEventEmitter = di.get(TypesCore.dispatcher);
        dispatcher.emit(GameEvents.SELECTABLE_ITEM_CHANGED);
    }

    public getColorByPlayerType(playerType: PlayerType): PlayerColor {
        let result: PlayerColor = GameColors.players[playerType];
        if (playerType === PlayerType.First) {
            if (this.selectedColorSet?.data) {
                result = this.selectedColorSet.data;
            }
        } else if (playerType === PlayerType.Second) {
            if (this.selectedColorSet?.id) {
                let colorData = SelectableColorsDataSet.filter(
                    item => item.id === UserSelectableColorsSet.BLUE
                );

                if (this.selectedColorSet.id === UserSelectableColorsSet.BLUE) {
                    colorData = SelectableColorsDataSet.filter(
                        item => item.id === UserSelectableColorsSet.RED
                    );
                }

                if (colorData.length) {
                    result = colorData[0].data;
                }
            }
        }

        return result;
    }

    /**
     * Temporary game level fix of undefined in cookies
     */
    protected get(key: string): Value {
        let result: Value = super.get(key);
        const valueIsUndefined = result === undefined || result === "undefined";
        if (valueIsUndefined) {
            const defaults = this.getDefaultStore();
            //@ts-ignore
            result = defaults[key];
        }
        return result;
    }
}
